import React from 'react'
import jsPDF from 'jspdf'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Table } from 'react-bootstrap'
import {
  fetchActions,
  fetchDistributors,
  fetchFindings,
  fetchTargets,
  type ActionListDto,
  type DistributorDto,
  type DistributorActionReportRunDto,
  type TargetDto,
  createDistributorActionReportRun,
  deleteDistributorActionReportRun,
  fetchDistributorActionReportRuns,
  logDistributorActionReportExport,
  fetchDistributorEmailTemplates,
  createDistributorEmailTemplate,
  sendDistributorReportEmail,
} from '../services/api'
import { MimCard, MimPage } from '../components/MimUi'
import '../styles/pageShellCompact.css'

type ReportRow = {
  distributorName: string
  distributorDomain: string
  findingId: string
  findingStatus: string
  severity: string
  description: string
  clause: string
  targetUrl: string
  actionCount: number
  actionStatuses: string
  actionAssignees: string
  actionDueDates: string
  actionDescriptions: string
}

export default function DistributorActionReports() {
  const queryClient = useQueryClient()
  const { data: findingsPage, isLoading: findingsLoading } = useQuery({
    queryKey: ['findings', 'reporting'],
    queryFn: () => fetchFindings({ pageSize: 500 }),
  })
  const findings = findingsPage?.items ?? []
  const { data: actions = [] } = useQuery<ActionListDto[]>({
    queryKey: ['actions', 'reporting'],
    queryFn: () => fetchActions(),
  })
  const { data: targets = [] } = useQuery<TargetDto[]>({
    queryKey: ['targets', 'reporting'],
    queryFn: fetchTargets,
  })
  const { data: distributors = [] } = useQuery<DistributorDto[]>({
    queryKey: ['distributors', 'reporting'],
    queryFn: fetchDistributors,
  })
  const { data: savedRuns = [] } = useQuery<DistributorActionReportRunDto[]>({
    queryKey: ['distributor-action-report-runs'],
    queryFn: () => fetchDistributorActionReportRuns(),
  })

  const [reportName, setReportName] = React.useState('')
  const [distributorId, setDistributorId] = React.useState('')
  const [severity, setSeverity] = React.useState('All')
  const [findingStatus, setFindingStatus] = React.useState('All')
  const [actionStatus, setActionStatus] = React.useState('All')
  const [introText, setIntroText] = React.useState('')
  const [outroText, setOutroText] = React.useState('')
  const [statusMessage, setStatusMessage] = React.useState<string | null>(null)
  const [selectedSavedRunId, setSelectedSavedRunId] = React.useState<string | null>(null)
  const [templateId, setTemplateId] = React.useState('')
  const [templateForm, setTemplateForm] = React.useState({
    name: '',
    subject: 'Compliance exception report — {{DistributorName}}',
    htmlBody: '<p>Dear partner,</p><p>Please find the attached exception report for {{DistributorName}} ({{DistributorDomain}}).</p>',
  })
  const [emailMessage, setEmailMessage] = React.useState<string | null>(null)

  const { data: emailTemplates = [] } = useQuery({
    queryKey: ['distributor-email-templates'],
    queryFn: fetchDistributorEmailTemplates,
  })

  const targetById = React.useMemo(() => new Map(targets.map(t => [t.id, t] as const)), [targets])
  const distributorById = React.useMemo(() => new Map(distributors.map(d => [d.id, d] as const)), [distributors])
  const actionsByFindingId = React.useMemo(() => {
    const map = new Map<string, ActionListDto[]>()
    for (const action of actions) {
      const arr = map.get(action.findingId) ?? []
      arr.push(action)
      map.set(action.findingId, arr)
    }
    return map
  }, [actions])

  const distributorOptions = React.useMemo(() => {
    const ids = new Set<string>()
    for (const f of findings) {
      const dId = targetById.get(f.targetId)?.distributorId
      if (dId) ids.add(dId)
    }
    return Array.from(ids)
      .map(id => ({
        id,
        label: distributorById.get(id)?.name ?? distributorById.get(id)?.domain ?? id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [findings, targetById, distributorById])

  const reportRows = React.useMemo<ReportRow[]>(() => {
    return findings
      .filter((f) => {
        const target = targetById.get(f.targetId)
        const matchesDistributor = !!distributorId && target?.distributorId === distributorId
        const matchesSeverity = severity === 'All' || f.severity === severity
        const matchesFindingStatus = findingStatus === 'All' || f.status === findingStatus
        const linkedActions = actionsByFindingId.get(f.id) ?? []
        const matchesActionStatus =
          actionStatus === 'All' ||
          linkedActions.some(a => a.status === actionStatus)
        return matchesDistributor && matchesSeverity && matchesFindingStatus && matchesActionStatus
      })
      .map((f) => {
        const target = targetById.get(f.targetId)
        const distributor = target ? distributorById.get(target.distributorId) : undefined
        const linkedActions = actionsByFindingId.get(f.id) ?? []
        return {
          distributorName: distributor?.name ?? '',
          distributorDomain: distributor?.domain ?? '',
          findingId: f.id,
          findingStatus: f.status ?? '',
          severity: f.severity ?? '',
          description: f.description ?? '',
          clause: f.mimClauseMappings ?? '',
          targetUrl: f.targetUrl ?? target?.url ?? '',
          actionCount: linkedActions.length,
          actionStatuses: linkedActions.map(a => a.status).join('; '),
          actionAssignees: linkedActions.map(a => a.assignedTo || '').filter(Boolean).join('; '),
          actionDueDates: linkedActions.map(a => a.dueDate ? new Date(a.dueDate).toLocaleDateString() : '').filter(Boolean).join('; '),
          actionDescriptions: linkedActions.map(a => a.description || '').filter(Boolean).join(' | '),
        }
      })
      .sort((a, b) => {
        const sev = severityRank(a.severity) - severityRank(b.severity)
        if (sev !== 0) return sev
        return a.findingId.localeCompare(b.findingId)
      })
  }, [findings, targetById, distributorById, actionsByFindingId, distributorId, severity, findingStatus, actionStatus])

  const selectedDistributor = distributorId ? distributorById.get(distributorId) : undefined

  const summary = React.useMemo(() => {
    const actionRows = reportRows.filter(r => r.actionCount > 0)
    return {
      findings: reportRows.length,
      actionLinkedFindings: actionRows.length,
      totalActions: reportRows.reduce((sum, r) => sum + r.actionCount, 0),
      critical: reportRows.filter(r => r.severity === 'Critical').length,
      high: reportRows.filter(r => r.severity === 'High').length,
    }
  }, [reportRows])

  const createRunMutation = useMutation({
    mutationFn: () =>
      createDistributorActionReportRun({
        distributorId,
        name: reportName.trim() || `Distributor action report - ${selectedDistributor?.name ?? distributorId}`,
        severityFilter: severity,
        findingStatusFilter: findingStatus,
        actionStatusFilter: actionStatus,
        introText: introText.trim() || undefined,
        outroText: outroText.trim() || undefined,
      }),
    onSuccess: (run) => {
      queryClient.invalidateQueries({ queryKey: ['distributor-action-report-runs'] })
      setSelectedSavedRunId(run.id)
      setStatusMessage('Report run saved to backend.')
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        (typeof error?.response?.data === 'string' ? error.response.data : undefined) ||
        error?.message ||
        'Failed to save report run.'
      setStatusMessage(message)
    },
  })

  const deleteRunMutation = useMutation({
    mutationFn: (id: string) => deleteDistributorActionReportRun(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distributor-action-report-runs'] })
      setStatusMessage('Saved report run deleted.')
    },
    onError: () => setStatusMessage('Failed to delete saved report run.'),
  })

  const exportAuditMutation = useMutation({
    mutationFn: (format: 'CSV' | 'PDF') =>
      logDistributorActionReportExport({
        distributorId,
        reportRunId: selectedSavedRunId ?? undefined,
        format,
        rowCount: reportRows.length,
        severityFilter: severity,
        findingStatusFilter: findingStatus,
        actionStatusFilter: actionStatus,
      }),
  })

  const saveReportRun = () => {
    if (!distributorId) {
      setStatusMessage('Select a distributor before saving a report run.')
      return
    }
    createRunMutation.mutate()
  }

  const loadReportRun = (run: DistributorActionReportRunDto) => {
    setReportName(run.name)
    setDistributorId(run.distributorId)
    setSeverity(run.severityFilter)
    setFindingStatus(run.findingStatusFilter)
    setActionStatus(run.actionStatusFilter)
    setIntroText(run.introText ?? '')
    setOutroText(run.outroText ?? '')
    setSelectedSavedRunId(run.id)
    setStatusMessage(`Loaded saved report run: ${run.name}`)
  }

  const deleteReportRun = (id: string) => {
    deleteRunMutation.mutate(id)
  }

  const exportCsv = () => {
    if (reportRows.length === 0) {
      setStatusMessage('No report rows to export.')
      return
    }
    const headers = [
      'Distributor Name', 'Distributor Domain', 'Finding ID', 'Finding Status', 'Severity',
      'Description', 'Clause', 'Target URL', 'Action Count', 'Action Statuses',
      'Action Assignees', 'Action Due Dates', 'Action Descriptions',
    ]
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`
    const lines = [
      headers.map(esc).join(','),
      ...reportRows.map(r => [
        r.distributorName, r.distributorDomain, r.findingId, r.findingStatus, r.severity,
        r.description, r.clause, r.targetUrl, r.actionCount, r.actionStatuses,
        r.actionAssignees, r.actionDueDates, r.actionDescriptions,
      ].map(esc).join(',')),
    ]
    downloadBlob(new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' }), buildFileName(selectedDistributor, 'csv'))
    exportAuditMutation.mutate('CSV')
  }

  const exportPdf = () => {
    if (!selectedDistributor) {
      setStatusMessage('Select a distributor before exporting PDF.')
      return
    }
    try {
      const pdf = new jsPDF('p', 'pt', 'a4')
      buildPdfDocument(pdf, {
        reportName: reportName.trim() || 'Distributor Action Report',
        distributorName: selectedDistributor.name ?? selectedDistributor.domain,
        distributorDomain: selectedDistributor.domain,
        generatedUtc: new Date().toISOString(),
        filters: { severity, findingStatus, actionStatus },
        summary,
        rows: reportRows,
        introText: introText.trim() || undefined,
        outroText: outroText.trim() || undefined,
      })
      const filename = buildFileName(selectedDistributor, 'pdf')
      pdf.save(filename)
      exportAuditMutation.mutate('PDF')
    } catch (err: any) {
      console.error('Failed to generate PDF', err)
      setStatusMessage('Failed to generate PDF report in the browser.')
    }
  }

  const findingStatusOptions = React.useMemo(() => uniqueOptions(findings.map(f => f.status)), [findings])
  const actionStatusOptions = React.useMemo(() => uniqueOptions(actions.map(a => a.status)), [actions])

  const sendEmail = async (testToSelf: boolean) => {
    if (!distributorId) {
      setEmailMessage('Select a distributor first.')
      return
    }
    if (!templateId) {
      setEmailMessage('Select or create an email template first.')
      return
    }
    try {
      const pdf = new jsPDF('p', 'pt', 'a4')
      buildPdfDocument(pdf, {
        reportName: reportName || `Distributor report - ${selectedDistributor?.name ?? ''}`,
        distributorName: selectedDistributor?.name ?? '',
        distributorDomain: selectedDistributor?.domain ?? '',
        generatedUtc: new Date().toISOString(),
        filters: { severity, findingStatus, actionStatus },
        summary,
        rows: reportRows,
        introText,
        outroText,
      })
      const pdfBase64 = pdf.output('datauristring').split(',')[1] || ''
      const res = await sendDistributorReportEmail({
        distributorId,
        templateId,
        testToSelf,
        toEmail: selectedDistributor?.primaryContactEmail || selectedDistributor?.marketingEmail || undefined,
        pdfBase64,
        pdfFileName: `distributor-report-${selectedDistributor?.domain || distributorId}.pdf`,
      })
      setEmailMessage(res.message)
    } catch (error: any) {
      setEmailMessage(error?.response?.data?.message || error?.message || 'Failed to send email.')
    }
  }

  return (
    <MimPage title="Distributor report" subtitle="Template → distributor → email PDF" compactHeader>
    <Container className="py-0 page-shell-compact">
      {statusMessage && <Alert variant="info" className="py-2 mb-2">{statusMessage}</Alert>}
      {emailMessage && <Alert variant="secondary" className="py-2 mb-2">{emailMessage}</Alert>}

      <div className="mim-stack" style={{ marginBottom: 12 }}>
        <MimCard title="1. Email template">
          <div className="mim-grid-2">
            <label>
              <span className="mim-label">Use template</span>
              <select className="mim-select" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                <option value="">Select template…</option>
                {emailTemplates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </label>
            <div className="mim-stack">
              <input className="mim-input" placeholder="New template name" value={templateForm.name} onChange={(e) => setTemplateForm((f) => ({ ...f, name: e.target.value }))} />
              <input className="mim-input" placeholder="Subject" value={templateForm.subject} onChange={(e) => setTemplateForm((f) => ({ ...f, subject: e.target.value }))} />
              <textarea className="mim-input" rows={3} value={templateForm.htmlBody} onChange={(e) => setTemplateForm((f) => ({ ...f, htmlBody: e.target.value }))} />
              <button
                type="button"
                className="mim-btn mim-btn-secondary"
                onClick={async () => {
                  if (!templateForm.name.trim() || !templateForm.subject.trim()) {
                    setEmailMessage('Template name and subject are required.')
                    return
                  }
                  const created = await createDistributorEmailTemplate(templateForm)
                  queryClient.invalidateQueries({ queryKey: ['distributor-email-templates'] })
                  setTemplateId(created.id)
                  setEmailMessage(`Template "${created.name}" created.`)
                }}
              >
                Save template
              </button>
            </div>
          </div>
        </MimCard>

        <MimCard title="2. Select distributor & exceptions" subtitle="Exception rows for the selected distributor appear below.">
          <Form.Select value={distributorId} onChange={e => setDistributorId(e.target.value)}>
            <option value="">Select distributor...</option>
            {distributorOptions.map(option => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </Form.Select>
        </MimCard>

        <MimCard title="3. Send email with PDF">
          <div className="mim-toolbar">
            <button type="button" className="mim-btn mim-btn-secondary" disabled={!distributorId || !templateId} onClick={() => void sendEmail(true)}>
              Send test email to me
            </button>
            <button type="button" className="mim-btn mim-btn-primary" disabled={!distributorId || !templateId || reportRows.length === 0} onClick={() => void sendEmail(false)}>
              Email distributor with PDF
            </button>
          </div>
        </MimCard>
      </div>

      <Card className="mb-2">
        <Card.Body>
          <Card.Title>Report setup</Card.Title>
          <Row className="g-2">
            <Col md={4}>
              <Form.Label className="small">Report name</Form.Label>
              <Form.Control value={reportName} onChange={e => setReportName(e.target.value)} placeholder="Optional saved report name" />
            </Col>
            <Col md={4}>
              <Form.Label className="small">Distributor</Form.Label>
              <Form.Select value={distributorId} onChange={e => setDistributorId(e.target.value)}>
                <option value="">Select distributor...</option>
                {distributorOptions.map(option => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label className="small">Severity</Form.Label>
              <Form.Select value={severity} onChange={e => setSeverity(e.target.value)}>
                <option value="All">All severities</option>
                <option value="Critical">Critical</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label className="small">Finding status</Form.Label>
              <Form.Select value={findingStatus} onChange={e => setFindingStatus(e.target.value)}>
                <option value="All">All finding statuses</option>
                {findingStatusOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label className="small">Action status</Form.Label>
              <Form.Select value={actionStatus} onChange={e => setActionStatus(e.target.value)}>
                <option value="All">All action statuses</option>
                {actionStatusOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </Form.Select>
            </Col>
            <Col md={4} className="d-flex align-items-end gap-2 flex-wrap">
              <Button size="sm" variant="outline-secondary" onClick={saveReportRun} disabled={!distributorId}>Save report run</Button>
              <Button size="sm" variant="outline-primary" onClick={exportCsv} disabled={reportRows.length === 0}>Export CSV</Button>
              <Button size="sm" variant="primary" onClick={exportPdf} disabled={!distributorId || reportRows.length === 0}>Export PDF</Button>
            </Col>
            <Col md={12}>
              <Form.Label className="small">Report introduction (top of PDF)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={introText}
                placeholder="Optional introductory text for this distributor report (appears at the top of the PDF)."
                onChange={e => setIntroText(e.target.value)}
              />
            </Col>
            <Col md={12}>
              <Form.Label className="small">Closing notes / next steps (bottom of PDF)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={outroText}
                placeholder="Optional closing text outlining conclusions, next steps, or sign‑off (appears at the end of the PDF)."
                onChange={e => setOutroText(e.target.value)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row className="g-2 mb-2">
        <Col md={2}>
          <Card body className="page-shell-stat-card">
            <div className="page-shell-stat-label">Findings</div>
            <div className="page-shell-stat-value">{summary.findings}</div>
          </Card>
        </Col>
        <Col md={2}>
          <Card body className="page-shell-stat-card">
            <div className="page-shell-stat-label">Linked actions</div>
            <div className="page-shell-stat-value">{summary.totalActions}</div>
          </Card>
        </Col>
        <Col md={2}>
          <Card body className="page-shell-stat-card">
            <div className="page-shell-stat-label">Findings w/ actions</div>
            <div className="page-shell-stat-value">{summary.actionLinkedFindings}</div>
          </Card>
        </Col>
        <Col md={2}>
          <Card body className="page-shell-stat-card">
            <div className="page-shell-stat-label">Critical</div>
            <div className="page-shell-stat-value text-danger">{summary.critical}</div>
          </Card>
        </Col>
        <Col md={2}>
          <Card body className="page-shell-stat-card">
            <div className="page-shell-stat-label">High</div>
            <div className="page-shell-stat-value text-warning">{summary.high}</div>
          </Card>
        </Col>
        <Col md={2}>
          <Card body className="page-shell-stat-card">
            <div className="page-shell-stat-label">Saved runs</div>
            <div className="page-shell-stat-value">{savedRuns.length}</div>
          </Card>
        </Col>
      </Row>

      <Card className="mb-2">
        <Card.Body>
          <Card.Title>Saved report runs</Card.Title>
          {savedRuns.length === 0 ? (
            <div className="text-muted small">No saved report runs yet. Save a configured report to reuse it later.</div>
          ) : (
            <Table size="sm" hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Distributor</th>
                  <th>Filters</th>
                  <th>Saved</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {savedRuns.map(run => {
                  const d = distributorById.get(run.distributorId)
                  return (
                    <tr key={run.id}>
                      <td>{run.name}</td>
                      <td>{d?.name ?? d?.domain ?? run.distributorId}</td>
                      <td className="small">
                        <Badge bg="secondary" className="me-1">Severity: {run.severityFilter}</Badge>
                        <Badge bg="secondary" className="me-1">Finding: {run.findingStatusFilter}</Badge>
                        <Badge bg="secondary">Action: {run.actionStatusFilter}</Badge>
                      </td>
                      <td>{new Date(run.createdUtc).toLocaleString()}</td>
                      <td className="text-end">
                        <Button size="sm" variant="outline-primary" className="me-2" onClick={() => loadReportRun(run)}>Load</Button>
                        <Button size="sm" variant="outline-danger" disabled={deleteRunMutation.isPending} onClick={() => deleteReportRun(run.id)}>Delete</Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <Card.Title>Report preview</Card.Title>
          {!distributorId && (
            <Alert variant="info" className="mb-0">Select a distributor to preview the action report.</Alert>
          )}
          {distributorId && findingsLoading && (
            <div className="text-muted small">Loading report data...</div>
          )}
          {distributorId && !findingsLoading && reportRows.length === 0 && (
            <Alert variant="warning" className="mb-0">No rows match the current filters for this distributor.</Alert>
          )}
          {reportRows.length > 0 && (
            <Table size="sm" striped hover responsive>
              <thead>
                <tr>
                  <th>Severity</th>
                  <th>Finding Status</th>
                  <th>Description</th>
                  <th>Target URL</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reportRows.map((row) => (
                  <tr key={row.findingId}>
                    <td><Badge bg={severityBadge(row.severity)}>{row.severity}</Badge></td>
                    <td>{row.findingStatus}</td>
                    <td>
                      <div>{row.description}</div>
                      {row.clause && <div className="small text-muted">Clause: {row.clause}</div>}
                    </td>
                    <td className="small text-break">{row.targetUrl || '-'}</td>
                    <td>
                      <div>{row.actionCount}</div>
                      {row.actionStatuses && <div className="small text-muted">{row.actionStatuses}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Container>
    </MimPage>
  )
}

function uniqueOptions(values: string[]) {
  return Array.from(new Set(values.filter(Boolean))).sort((a, b) => a.localeCompare(b))
}

function severityRank(severity: string) {
  switch (severity) {
    case 'Critical': return 0
    case 'High': return 1
    case 'Medium': return 2
    case 'Low': return 3
    default: return 9
  }
}

function severityBadge(severity: string) {
  switch (severity) {
    case 'Critical': return 'danger'
    case 'High': return 'warning'
    case 'Medium': return 'info'
    case 'Low': return 'secondary'
    default: return 'dark'
  }
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function buildFileName(distributor?: DistributorDto, ext: 'csv' | 'pdf' = 'csv') {
  const slug = (distributor?.name ?? distributor?.domain ?? 'distributor')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return `distributor-action-report-${slug || 'distributor'}-${new Date().toISOString().slice(0, 10)}.${ext}`
}

export function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function buildPdfDocument(
  pdf: jsPDF,
  args: {
    reportName: string
    distributorName: string
    distributorDomain: string
    generatedUtc: string
    filters: { severity: string; findingStatus: string; actionStatus: string }
    summary: { findings: number; actionLinkedFindings: number; totalActions: number; critical: number; high: number }
    rows: ReportRow[]
    introText?: string
    outroText?: string
  },
) {
  const lineHeight = 16
  const pageWidth = pdf.internal.pageSize.getWidth()
  const margin = 32
  const maxWidth = pageWidth - margin * 2
  let y = margin

  const addHeading = (text: string, size = 16) => {
    pdf.setFontSize(size)
    pdf.setFont('Helvetica', 'bold')
    pdf.text(text, margin, y)
    y += lineHeight
  }

  const addParagraph = (text: string, size = 11) => {
    if (!text.trim()) return
    pdf.setFontSize(size)
    pdf.setFont('Helvetica', 'normal')
    const lines = pdf.splitTextToSize(text, maxWidth)
    for (const line of lines) {
      if (y > pdf.internal.pageSize.getHeight() - margin) {
        pdf.addPage()
        y = margin
      }
      pdf.text(line, margin, y)
      y += lineHeight
    }
    y += 4
  }

  const addKeyValue = (label: string, value: string) => {
    pdf.setFontSize(11)
    pdf.setFont('Helvetica', 'bold')
    const labelWidth = pdf.getTextWidth(`${label}: `)
    pdf.text(`${label}:`, margin, y)
    pdf.setFont('Helvetica', 'normal')
    pdf.text(value, margin + labelWidth + 2, y)
    y += lineHeight
  }

  const ensureSpace = (needed: number) => {
    if (y + needed > pdf.internal.pageSize.getHeight() - margin) {
      pdf.addPage()
      y = margin
    }
  }

  // Header
  addHeading(args.reportName, 18)
  pdf.setFontSize(11)
  pdf.setFont('Helvetica', 'normal')
  addKeyValue('Distributor', `${args.distributorName} (${args.distributorDomain})`)
  addKeyValue('Generated', new Date(args.generatedUtc).toLocaleString())
  addParagraph(
    `Filters: Severity=${args.filters.severity}, Finding status=${args.filters.findingStatus}, Action status=${args.filters.actionStatus}`,
  )

  // Intro
  if (args.introText) {
    addHeading('Overview', 14)
    addParagraph(args.introText)
  }

  // Summary tiles
  ensureSpace(lineHeight * 4)
  addHeading('Summary', 14)
  addParagraph(
    `Findings: ${args.summary.findings} | Linked actions: ${args.summary.totalActions} | Findings with actions: ${args.summary.actionLinkedFindings} | Critical: ${args.summary.critical} | High: ${args.summary.high}`,
  )

  // Findings table as text rows
  if (args.rows.length > 0) {
    ensureSpace(lineHeight * 3)
    addHeading('Findings and Actions', 14)
    pdf.setFontSize(10)
    pdf.setFont('Helvetica', 'bold')
    const headers = ['Severity', 'Status', 'Description', 'Target URL', 'Actions']
    const colWidths = [60, 60, 180, 160, 60]
    let x = margin
    headers.forEach((h, i) => {
      pdf.text(h, x, y)
      x += colWidths[i]
    })
    y += lineHeight
    pdf.setFont('Helvetica', 'normal')

    for (const row of args.rows) {
      ensureSpace(lineHeight * 3)
      const desc = row.description || ''
      const clauseSuffix = row.clause ? ` (Clause: ${row.clause})` : ''
      const descLines = pdf.splitTextToSize(desc + clauseSuffix, colWidths[2] - 4)
      const url = row.targetUrl || ''
      const urlLines = pdf.splitTextToSize(url, colWidths[3] - 4)
      const actionSummary =
        row.actionCount === 0 ? '0' : `${row.actionCount}: ${row.actionStatuses || ''}`.trim()
      const actionLines = pdf.splitTextToSize(actionSummary, colWidths[4] - 4)
      const maxLines = Math.max(descLines.length, urlLines.length, actionLines.length, 1)

      for (let i = 0; i < maxLines; i++) {
        x = margin
        if (i === 0) {
          pdf.text(row.severity || '', x, y)
          x += colWidths[0]
          pdf.text(row.findingStatus || '', x, y)
          x += colWidths[1]
        } else {
          x += colWidths[0] + colWidths[1]
        }
        if (descLines[i]) pdf.text(descLines[i], x, y)
        x += colWidths[2]
        if (urlLines[i]) pdf.text(urlLines[i], x, y)
        x += colWidths[3]
        if (actionLines[i]) pdf.text(actionLines[i], x, y)
        y += lineHeight
      }
      y += 4
    }
  }

  // Outro on new page
  if (args.outroText) {
    pdf.addPage()
    y = margin
    addHeading('Conclusions & Next Steps', 14)
    addParagraph(args.outroText)
  }
}

export function buildPrintHtml(_args: {
  reportName: string
  distributorName: string
  distributorDomain: string
  generatedUtc: string
  filters: { severity: string; findingStatus: string; actionStatus: string }
  summary: { findings: number; actionLinkedFindings: number; totalActions: number; critical: number; high: number }
  rows: ReportRow[]
  introText?: string
  outroText?: string
}) {
  // Legacy helper retained for backwards compatibility; PDF export is now the default path.
  return ''
}
